import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { viewTransition } from '@theme/utilities';

const AS_RETAINED_SLIDE = 'data-as-retained-slide';

/** View labels may identify corresponding media across a product's variants. */
function getGalleryView(slide) {
  const alt = (slide?.querySelector('img')?.getAttribute('alt') || '').toLowerCase();
  if (/\bon[- ]ear\b|\bon[- ]body\b|\bbody view\b/.test(alt)) return 'body';
  const numberedView = alt.match(/\bview\s+(\d+)\b/);
  if (numberedView) return `view-${numberedView[1]}`;
  if (/\bon hand\b|\bhand view\b|\bon finger\b/.test(alt)) return 'hand';
  if (/\bfront view\b/.test(alt)) return 'front';
  if (/\bprofile view\b/.test(alt)) return 'profile';
  if (/\bside view\b/.test(alt)) return 'side';
  if (/\bback view\b/.test(alt)) return 'back';
  return null;
}
function getImageSource(slide) {
  return (slide?.querySelector('img')?.getAttribute('src') || '').split('?')[0];
}
function normalizeProductId(id) { return String(id ?? '').split('/').pop(); }

/**
 * Product media gallery. Preserve the selected view only when the same product
 * receives new variant media; keep each product's own media and image mapping.
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });
    this.addEventListener('pointerdown', this.#cancelRetainedView, { signal });
    this.#restoreRetainedView().catch((error) => {
      this.removeAttribute(AS_RETAINED_SLIDE);
      console.warn('[media-gallery] Could not restore selected view:', error);
    });
  }
  #controller = new AbortController();
  #selectionGeneration = 0;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#selectionGeneration += 1;
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#selectionGeneration;
    const section = this.closest('.shopify-section');
    const primaryForm = section?.querySelector('.product-details product-form-component');
    const currentProductId = normalizeProductId(primaryForm?.dataset.productId);
    event.promise
      .then(({ detail }) => {
        if (!detail?.html || !this.isConnected || generation !== this.#selectionGeneration) return;
        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;
        const nextProductId = normalizeProductId(detail.newProduct?.id ?? detail.productId ??
          html.querySelector('product-form-component')?.dataset.productId);
        if (currentProductId && nextProductId === currentProductId) {
          this.#prepareRetainedView(newMediaGallery);
        }
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #prepareRetainedView(newGallery) {
    const currentSlideshow = this.slideshow;
    const newSlideshow = newGallery.querySelector('slideshow-component[ref="slideshow"]');
    if (!currentSlideshow || !newSlideshow) return;
    const pendingIndex = this.getAttribute(AS_RETAINED_SLIDE);
    const currentIndex = pendingIndex === null ? currentSlideshow.current : Number(pendingIndex);
    const currentSlides = currentSlideshow.slides || [];
    const currentSlide = currentSlides[currentIndex];
    if (!Number.isInteger(currentIndex) || !currentSlide) return;
    const slides = Array.from(newSlideshow.querySelectorAll('slideshow-slide')).filter(
      (slide) => slide.closest('slideshow-component') === newSlideshow && !slide.hasAttribute('hidden')
    );
    if (!slides.length) return;
    const currentSource = getImageSource(currentSlide);
    const currentMediaId = currentSlide.getAttribute('data-media-id');
    const view = getGalleryView(currentSlide);
    let index = currentMediaId ? slides.findIndex((slide) => slide.getAttribute('data-media-id') === currentMediaId) : -1;
    if (index < 0 && currentSource) index = slides.findIndex((slide) => getImageSource(slide) === currentSource);
    if (index < 0 && view) index = slides.findIndex((slide) => getGalleryView(slide) === view);
    if (index < 0) index = slides.length === currentSlides.length ? currentIndex : 0;
    if (!slides[index]) index = 0;
    newSlideshow.setAttribute('initial-slide', String(index));
    slides.forEach((slide, position) => {
      if (!slide.hasAttribute('slide-id')) slide.setAttribute('slide-id', `as-product-view-${position}`);
      slide.setAttribute('aria-hidden', String(position !== index));
    });
    newSlideshow.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumbnail) => {
      const position = Number(thumbnail.getAttribute('on:click')?.split('/').pop());
      thumbnail.setAttribute('aria-selected', String(position === index));
    });
    slides[index].querySelectorAll('img').forEach((image) => image.setAttribute('loading', 'eager'));
    if (index > 0) newGallery.setAttribute(AS_RETAINED_SLIDE, String(index));
  }

  async #restoreRetainedView() {
    const retained = this.getAttribute(AS_RETAINED_SLIDE);
    if (retained === null) return;
    const index = Number(retained);
    await customElements.whenDefined('slideshow-component');
    if (viewTransition.current) await viewTransition.current;
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (!this.isConnected || this.getAttribute(AS_RETAINED_SLIDE) !== retained) return;
    const slideshow = this.slideshow;
    if (Number.isInteger(index) && index > 0 && slideshow?.slides?.[index]) {
      slideshow.current = 0;
      await slideshow.select(index, undefined, { animate: false });
    }
    this.removeAttribute(AS_RETAINED_SLIDE);
  }
  #cancelRetainedView = (event) => {
    if (event.target instanceof Element && event.target.closest('slideshow-component') === this.slideshow) {
      this.removeAttribute(AS_RETAINED_SLIDE);
    }
  };
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };
  zoom(index, event) { this.refs.zoomDialogComponent?.open(index, event); }
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }
  get slideshow() { return this.refs.slideshow; }
  get media() { return this.refs.media; }
  get presentation() { return this.dataset.presentation; }
}
if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
